import React from "react";
import { View, Text, StyleSheet, TouchableOpacity, Platform } from "react-native";
import { useTheme } from "../../theme";

// Card component interface
interface ObservationCardProps {
  item: any;
  columns: any[];
  onRowClick: (rowData: any) => void;
}

/**
 * ObservationCard Component
 * Displays observation data in a card format for mobile devices
 * Supports expandable content with "View Details" toggle
 */
const ObservationCard: React.FC<ObservationCardProps> = ({ item, columns, onRowClick }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const normalizeStatus = (status: string) =>
    String(status || "")
      .toLowerCase()
      .replace(/[-_\s]/g, "");

  // Get the status tag style based on status value
  const getStatusTagStyle = (status: string) => {
    switch (normalizeStatus(status)) {
      case "completed":
        return styles.statusTagCompleted;
      case "inprogress":
        return styles.statusTagInProgress;
      case "failed":
        return styles.statusTagFailed;
      default:
        return styles.statusTagDefault;
    }
  };

  // Get the status text color
  const getStatusTextColor = (status: string) => {
    switch (normalizeStatus(status)) {
      case "completed":
      case "failed":
      case "inprogress":
        return "#FFFFFF";
      default:
        return "#FFFFFF";
    }
  };

  // First column (details) - clickable for navigation
  const firstColumn = columns[0];
  const firstValue = firstColumn?.renderCell
    ? firstColumn.renderCell(item)
    : item[firstColumn?.columnKey];

  // Last column (status) - non-clickable
  const lastColumn = columns[columns.length - 1];
  const lastValue = item[lastColumn?.columnKey] || item.action || item.status || "new";

  // All columns for display (skip first and last columns)
  const displayColumns = columns.slice(1, columns.length - 1);

  return (
    <View style={[styles.cardContainer, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default, borderWidth: 1 }]}>
      {/* First Row: Primary value (left) + Status tag (right) */}
      <View style={styles.cardHeader}>
        <TouchableOpacity
          onPress={() => onRowClick({ ...item, columnKey: "details" })}
          style={styles.cardPrimaryValue}
        >
          <Text style={[styles.cardPrimaryText, isDark && { color: colors.brand.primary }]} numberOfLines={2}>
            {typeof firstValue === "string"
              ? firstValue
              : item.type || item.details || "Observation"}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={() => onRowClick({ ...item, columnKey: "details" })}
          style={[styles.statusTag, getStatusTagStyle(lastValue)]}
        >
          <Text
            style={[
              styles.statusTagText,
              { color: getStatusTextColor(lastValue) },
            ]}
          >
            {lastValue}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Content Rows: "Header: Value" format - show all fields */}
      <View style={styles.cardContent}>
        {displayColumns.map((column, index) => {
          const value = column.renderCell
            ? column.renderCell(item)
            : item[column.columnKey];

          return (
            <View key={`${column.columnKey}-${index}`} style={styles.cardRow}>
              <Text style={styles.cardRowText}>
                <Text style={[styles.cardRowLabel, isDark && { color: colors.text.secondary }]}>
                  {column.header.toUpperCase()}:{" "}
                </Text>
                  <Text style={[styles.cardRowValue, isDark && { color: colors.text.primary }]}>
                  {typeof value === "string" ? value : value || "-"}
                </Text>
              </Text>
            </View>
          );
        })}
      </View>

      {/* View Details Link - opens overlay when clicked */}
      {displayColumns.length > 0 && (
        <View style={styles.viewDetailsContainer}>
          <TouchableOpacity
            onPress={(e) => {
              e?.stopPropagation?.();
              onRowClick(item);
            }}
            style={styles.viewDetailsButton}
          >
            <Text style={[styles.viewDetailsText, isDark && { color: colors.brand.primary }]}>View Details</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Tap anywhere else overlay for modal - positioned to avoid View Details area */}
      <TouchableOpacity
        testID="card-tap-overlay"
        style={[
          styles.cardTapOverlay,
          {
            bottom: displayColumns.length > 0 ? 40 : 0, // Leave space for View Details
          },
        ]}
        onPress={() => onRowClick(item)}
        activeOpacity={0.1}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 16,
    marginBottom: 8,
    ...Platform.select({
      ios: {
        shadowColor: "rgba(0, 94, 192, 0.08)",
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 12,
      },
      android: {
        elevation: 4,
        shadowColor: "rgba(0, 94, 192, 0.08)",
      },
      web: {
        boxShadow: "4px 0px 24px -2px rgba(0, 94, 192, 0.08)",
      },
    }),
    position: "relative",
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  cardPrimaryValue: {
    flex: 1,
    marginRight: 12,
  },
  cardPrimaryText: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    fontWeight: "bold",
    color: "#005CBC",
    lineHeight: 20,
    textAlign: "left",
  },
  statusTag: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    minWidth: 50,
    alignItems: "center",
    justifyContent: "center",
  },
  statusTagText: {
    fontSize: 12,
    fontWeight: "400",
    textAlign: "center",
    lineHeight: 18,
  },
  statusTagCompleted: {
    backgroundColor: "#28A745",
  },
  statusTagInProgress: {
    backgroundColor: "#E5A408",
  },
  statusTagFailed: {
    backgroundColor: "#DC3545",
  },
  statusTagDefault: {
    backgroundColor: "#6C757D",
  },
  cardContent: {
    marginBottom: 8,
  },
  cardRow: {
    marginBottom: 4,
  },
  cardRowText: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    lineHeight: 20,
    textAlign: "left",
  },
  cardRowLabel: {
    fontWeight: "700",
    fontSize: Platform.OS === "web" ? 12 : 12,
    color: "#3A3A3A",
  },
  cardRowValue: {
    fontWeight: "400",
    fontSize: Platform.OS === "web" ? 14 : 12,
    color: "#3A3A3A",
  },
  viewDetailsContainer: {
    paddingVertical: 4,
    marginBottom: -6,
    zIndex: 10, // Ensure it's above the tap overlay
  },
  viewDetailsButton: {
    zIndex: 11, // Higher than container
  },
  viewDetailsText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#005CBC",
    lineHeight: 20,
  },
  cardTapOverlay: {
    position: "absolute",
    top: 60, // Start below the header section
    left: 0,
    right: 0,
    bottom: 0, // Will be dynamically adjusted
    backgroundColor: "transparent",
    zIndex: 1, // Higher z-index for reliable touch handling on iOS
  },
});

export default ObservationCard;
